import { XClose } from '@untitled-ui/icons-react/build/cjs';
import React, { memo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import ButtonCmp from 'src/components/ButtonCmp';
import { setActionTooltipGlobalOpen, setTooltipGlobalOpen } from 'src/redux/basic.slice';
import { RootState } from 'src/redux/store';

interface IProps {
    title?: any;
    handleClose(status: boolean): any;
    children: any;
    modalTitleDesc?: any;
    size?: string;
    headerPrimaryIcon?: any;
    headerPrimaryIconClass?: string;
    headerInfoClass?: string;
    modalHeaderClass?: string;
    modalFooterClass?: string;
    modalPrimaryBtnClass?: string;
    modalBtnTypeClass?: string;
    modalSecondaryBtnTextClass?: string;
    modalSecondaryBtnText2Class?: string;
    primaryBtnLoading?: boolean;
    primaryBtnDisabled?: boolean;
    primaryBtnText?: string;
    dangerBtnText?: string;
    primaryButtonIcon?: any;
    primaryBtnOnClick?: (e?: any) => void;
    modalClassName?: string;
    secondaryBtnLoading?: boolean;
    secondaryBtnDisabled?: boolean;
    secondaryBtnText?: string;
    secondaryBtnColor?: string;
    secondaryBtnIcon?: any;
    secondaryBtnIconSide?: string;
    secondaryBtnOnClick?: (e?: any) => void;
    ModalWithSidebar?: boolean;
    ContentLeft?: any;

    secondaryBtnLoading2?: boolean;
    secondaryBtnDisabled2?: boolean;
    secondaryBtnText2?: string;
    secondaryBtnColor2?: string;
    secondaryBtnOnClick2?: () => void;

    isOverflow?: boolean;
    closeBtnClass?: string;
    modalHeaderTextClass?: string;
    modalParentClass?: string;
    hideCloseBtn?: boolean;
    headerRightContent?: React.ReactNode;
    isEscapeClose?: boolean;
    childrenContainerId?: string;
    closeOnOutsideClick?: boolean;
    modalFooterCustomContent?: any;
    modalContentSidebarClass?: string;
}

const modalStack: any[] = [];

const CommonModal = ({
    size,
    handleClose,
    children,
    title,
    modalTitleDesc,
    headerPrimaryIcon,
    headerPrimaryIconClass,
    headerInfoClass,
    modalHeaderClass,
    modalFooterClass,
    modalPrimaryBtnClass,
    modalBtnTypeClass,
    primaryBtnLoading,
    primaryBtnDisabled,
    primaryBtnText,
    dangerBtnText,
    primaryButtonIcon,
    primaryBtnOnClick,
    secondaryBtnLoading,
    secondaryBtnDisabled,
    secondaryBtnText,
    secondaryBtnColor,
    secondaryBtnOnClick,
    secondaryBtnIcon,
    secondaryBtnIconSide = 'left',
    closeBtnClass,
    modalHeaderTextClass,
    modalSecondaryBtnTextClass,
    modalSecondaryBtnText2Class,
    isOverflow = true,
    secondaryBtnLoading2,
    secondaryBtnDisabled2,
    secondaryBtnText2,
    secondaryBtnColor2,
    modalClassName,
    modalParentClass,
    isEscapeClose = true,
    secondaryBtnOnClick2,
    childrenContainerId,
    closeOnOutsideClick = true,
    modalFooterCustomContent,
    ModalWithSidebar,
    ContentLeft,
    modalContentSidebarClass,
    hideCloseBtn = false,
    headerRightContent,
}: IProps) => {
    const [isOpen, setIsOpen] = useState(true);
    const [internalLoading, setInternalLoading] = useState(false);
    const isProcessingRef = useRef(false); // Synchronous loading check
    const focusableElements = useRef<any>([]);
    const modalIdRef = React.useRef(Symbol());

    const dispatch = useDispatch();
    const { tooltipGlobalOpen } = useSelector((state: RootState) => state.basic);

    useEffect(() => {
        if (isOpen) {
            modalStack.push(modalIdRef.current);
        }

        return () => {
            const idx = modalStack.indexOf(modalIdRef.current);
            if (idx > -1) modalStack.splice(idx, 1);
        };
    }, [isOpen]);

    useEffect(() => {
        // To Instantally hide all tooltips when modal is open
        const tooltipElements = document.querySelectorAll('.tippy-popper');
        tooltipElements.forEach((el) => {
            (el as HTMLElement).style.display = 'none';
        });

        setTimeout(() => {
            tooltipElements.forEach((el) => {
                (el as HTMLElement).style.display = '';
            });
        }, 300);
    }, []);

    useEffect(() => {
        if (isOpen) {
            const elements = Array.from(document.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter((el: any) => !el.closest('.modal'));

            focusableElements.current = elements;
            elements.forEach((el: any) => el.setAttribute('tabindex', '-1'));

            return () => {
                focusableElements.current.forEach((el: any) => el.removeAttribute('tabindex'));
            };
        }
    }, [isOpen]);

    useEffect(() => {
        if (isEscapeClose) {
            const handleEscapePress = (event: any) => {
                if (event.key === 'Escape') {
                    if (modalStack[modalStack.length - 1] === modalIdRef.current) {
                        handleClose(false);
                    }
                }
            };
            document.addEventListener('keydown', handleEscapePress);

            return () => {
                document.removeEventListener('keydown', handleEscapePress);
            };
        }
    }, [handleClose]);

    const handleCloseModal = (status: boolean) => {
        const rootElement = document.querySelector('#root');

        if (rootElement) {
            rootElement.classList.remove('tooltipShow');
        }
        // Reset loading state when modal closes
        isProcessingRef.current = false;
        setInternalLoading(false);
        setIsOpen(status);
        handleClose(status);
    };

    useEffect(() => {
        if (isOpen && tooltipGlobalOpen) {
            dispatch(setTooltipGlobalOpen(false));
            dispatch(setActionTooltipGlobalOpen(false));
        }
    }, [isOpen, tooltipGlobalOpen]);

    // Handle primary button click with automatic loading state management
    const handlePrimaryBtnClick = async (e?: any) => {
        // Synchronous check using ref to prevent race conditions
        if (!primaryBtnOnClick || isProcessingRef.current) return;

        // Set ref immediately (synchronous)
        isProcessingRef.current = true;
        setInternalLoading(true);

        try {
            await Promise.resolve(primaryBtnOnClick(e));
        } catch (error) {
            console.error('Primary button click error:', error);
        } finally {
            isProcessingRef.current = false;
            setInternalLoading(false);
        }
    };

    // Use external loading state if provided, otherwise use internal
    const isLoading = primaryBtnLoading ?? internalLoading;

    const ModalContentAndFooter = (
        <>
            {' '}
            <article id={childrenContainerId} className={`flex-1 scrollbar-hide ${modalClassName} ${isOverflow ? 'overflow-y-auto' : ''}`}>
                {children}
            </article>
            {(secondaryBtnText || primaryBtnText || dangerBtnText) && (
                <footer className={`p-4 border-t border-utilityGray200 bg-gray50 flex flex-wrap gap-3 justify-end ${modalFooterClass}`}>
                    {modalFooterCustomContent && modalFooterCustomContent}
                    {secondaryBtnText && (
                        <ButtonCmp
                            className={`${secondaryBtnColor ?? 'btn_secondary_black text-textSecondary'} ${modalSecondaryBtnTextClass}`}
                            onClick={secondaryBtnOnClick}
                            loading={secondaryBtnLoading}
                            disabled={secondaryBtnDisabled || isLoading}
                            icon={secondaryBtnIcon}
                            iconSide={secondaryBtnIconSide}
                        >
                            {secondaryBtnText}
                        </ButtonCmp>
                    )}
                    {secondaryBtnText2 && (
                        <ButtonCmp
                            className={`${secondaryBtnColor2 ?? 'btn_secondary_black text-textSecondary '} ${modalSecondaryBtnText2Class}`}
                            onClick={secondaryBtnOnClick2}
                            loading={secondaryBtnLoading2}
                            disabled={secondaryBtnDisabled2}
                        >
                            {secondaryBtnText2}
                        </ButtonCmp>
                    )}
                    {primaryBtnText && (
                        <ButtonCmp
                            className={`${modalPrimaryBtnClass} ${modalBtnTypeClass ? modalBtnTypeClass : 'btn_primary '}`}
                            onClick={handlePrimaryBtnClick}
                            loading={isLoading}
                            disabled={isLoading || primaryBtnDisabled}
                            icon={primaryButtonIcon}
                        >
                            {primaryBtnText}
                        </ButtonCmp>
                    )}
                    {dangerBtnText && (
                        <ButtonCmp className="btn_danger" onClick={handlePrimaryBtnClick} loading={isLoading} disabled={isLoading || primaryBtnDisabled} autofocus={true}>
                            {dangerBtnText}
                        </ButtonCmp>
                    )}
                </footer>
            )}
        </>
    );

    const modalContent = (
        <dialog className={`modal modal-root ${modalParentClass}`} open>
            <div className="fixed inset-0 bg-utilityBlack bg-opacity-50 model-backdrop-remove" onClick={closeOnOutsideClick ? () => handleCloseModal(false) : undefined}></div>
            <div className={`modal-box rounded-2xl [&>header]:rounded-t-2xl [&>footer]:rounded-b-2xl ${size} p-0 flex flex-col custom-scrollbar-v2`} onClick={(e) => e.stopPropagation()}>
                {title && (
                    <header className={`flex justify-between border-b px-4 py-2.5 bg-gray50 ${modalHeaderClass}`}>
                        <div className={`flex xxl:gap-4 gap-3 md:flex-row ${modalHeaderTextClass}`}>
                            {headerPrimaryIcon && (
                                <div
                                    className={`md:h-[43px] h-8 md:w-[43px] w-8 relative z-[6] md:p-2 p-1.5 rounded-[10px] shadow-sm border border-gray10 text-textSecondary flex items-center justify-center flex-none ${headerPrimaryIconClass}`}
                                >
                                    {headerPrimaryIcon}
                                </div>
                            )}
                            <div className={`flex flex-col justify-center gap-1 ${headerInfoClass}`}>
                                <div className="text-grayLight900 text-base font-semibold break-words word-break">{title}</div>
                                <div className="text-grayLight600 text-xs font-normal leading-tight break-all break-words word-break empty:hidden">{modalTitleDesc}</div>
                            </div>
                        </div>
                        {headerRightContent ? (
                            headerRightContent
                        ) : !hideCloseBtn ? (
                            <div
                                className={`cursor-pointer [&>svg]:w-4 [&>svg]:text-grayLight600 bg-transparent hover:bg-[#e5e5e5] transition-all duration-300 h-8 w-8 flex justify-center items-center rounded-full flex-none ${closeBtnClass}`}
                                onClick={() => handleCloseModal(false)}
                            >
                                <XClose className="text-gray400 hover:text-textSecondary" />
                            </div>
                        ) : null}
                    </header>
                )}
                {ModalWithSidebar ? (
                    <div className={`flex overflow-auto flex-1 ${modalContentSidebarClass}`}>
                        {ContentLeft}
                        <div className="overflow-auto flex-1 flex flex-col">{ModalContentAndFooter}</div>
                    </div>
                ) : (
                    ModalContentAndFooter
                )}
            </div>
        </dialog>
    );

    return createPortal(modalContent, document.body);
};

export default memo(CommonModal);
